import type { ModuleAction, ModulePermissions } from './Role';
import { MODULE_ACTIONS, ROLE_LABELS, actionsForModule } from './Role';

export interface RoleActor { uid: string; email: string; name: string }

export interface CustomRole {
  id: string; name: string; description: string; permissions: ModulePermissions; active: boolean;
  createdAt: Date | null; createdBy: RoleActor | null; updatedAt: Date | null; updatedBy: RoleActor | null;
}

/** Rol que se le puede asignar a un usuario: predefinido o personalizado. */
export interface AssignableRole { id: string; name: string; isPredefined: boolean }

export type RoleHistoryType =
  | 'role_created' | 'role_updated' | 'role_deactivated' | 'role_reactivated' | 'user_role_changed'
  | 'user_permissions_changed' | 'user_deactivated' | 'user_reactivated' | 'user_view_changed';

export interface RoleSnapshot { name: string; description: string; permissions: ModulePermissions; active: boolean }
export interface UserRoleSnapshot { roleId: string; roleName: string; hadIndividualPermissions?: boolean }
/** null = sin mapa individual (acceso completo). */
export interface UserPermissionsSnapshot { permissions: ModulePermissions | null }
export interface UserStatusSnapshot { status: 'active' | 'inactive' }
/** Vista de embarques asignada; null = sin vista (ve todos los embarques). */
export interface UserViewSnapshot { viewId: string | null; viewName: string | null }

export interface RoleHistoryEntry {
  id: string; type: RoleHistoryType;
  /** Rol afectado; en user_role_changed, el rol nuevo. */
  roleId: string; roleName: string;
  targetUser: { uid: string; email: string } | null;
  before: RoleSnapshot | UserRoleSnapshot | UserPermissionsSnapshot | UserStatusSnapshot | UserViewSnapshot | null;
  after: RoleSnapshot | UserRoleSnapshot | UserPermissionsSnapshot | UserStatusSnapshot | UserViewSnapshot | null;
  actor: RoleActor; at: Date | null;
}

export const ROLE_NAME_MAX_LENGTH = 60;
export const ROLE_DESCRIPTION_MAX_LENGTH = 200;

const isPlainObject = (v: unknown): v is Record<string, any> =>
  !!v && typeof v === 'object' && !Array.isArray(v);

const toDate = (v: any): Date | null => (v && typeof v.toDate === 'function' ? v.toDate() : null);

const toActor = (v: any): RoleActor | null =>
  isPlainObject(v) && v.uid
    ? { uid: String(v.uid), email: String(v.email ?? ''), name: String(v.name ?? '') }
    : null;

export function normalizeCustomRole(id: string, data: any): CustomRole {
  return {
    id,
    name: String(data?.name ?? '').trim(),
    description: String(data?.description ?? '').trim(),
    permissions: isPlainObject(data?.permissions) ? (data.permissions as ModulePermissions) : {},
    active: data?.active !== false,
    createdAt: toDate(data?.createdAt),
    createdBy: toActor(data?.createdBy),
    updatedAt: toDate(data?.updatedAt),
    updatedBy: toActor(data?.updatedBy)
  };
}

export function customRoleHasAction(
  role: CustomRole | null | undefined,
  moduleId: string,
  action: ModuleAction
): boolean {
  if (!role || !role.active) return false;
  // Un documento escrito a mano puede traer acciones fuera del catálogo del módulo: no se conceden.
  if (!actionsForModule(moduleId).includes(action)) return false;
  return (role.permissions[moduleId] ?? []).includes(action);
}

/** Deja solo acciones válidas por módulo, sin duplicados, ordenadas y con 'read' si hay alguna. */
export function sanitizeRolePermissions(input: ModulePermissions): ModulePermissions {
  const result: ModulePermissions = {};
  for (const [moduleId, actions] of Object.entries(input ?? {})) {
    const valid = actionsForModule(moduleId);
    const unique = MODULE_ACTIONS.filter(a => valid.includes(a) && (actions ?? []).includes(a));
    if (!unique.length) continue;
    result[moduleId] = unique.includes('read') ? [...unique] : ['read', ...unique];
  }
  return result;
}

const normalizeName = (s: string) => s.trim().toLocaleLowerCase('es');

export function validateRoleName(
  name: string,
  existing: Array<Pick<CustomRole, 'id' | 'name'>>,
  selfId?: string
): string | null {
  const clean = normalizeName(name ?? '');
  if (!clean) return 'El nombre del rol es obligatorio';
  if (clean.length > ROLE_NAME_MAX_LENGTH) return 'El nombre no puede superar 60 caracteres';
  if (Object.values(ROLE_LABELS).some(label => normalizeName(label) === clean)) {
    return 'Ese nombre está reservado para un rol predefinido';
  }
  if (existing.some(r => r.id !== selfId && normalizeName(r.name) === clean)) {
    return 'Ya existe un rol con ese nombre';
  }
  return null;
}

export function diffPermissions(
  before: ModulePermissions,
  after: ModulePermissions
): Array<{ module: string; added: ModuleAction[]; removed: ModuleAction[] }> {
  const modules = Array.from(new Set([...Object.keys(before ?? {}), ...Object.keys(after ?? {})])).sort();
  const result: Array<{ module: string; added: ModuleAction[]; removed: ModuleAction[] }> = [];
  for (const module of modules) {
    const b = before?.[module] ?? [];
    const a = after?.[module] ?? [];
    const added = MODULE_ACTIONS.filter(x => a.includes(x) && !b.includes(x));
    const removed = MODULE_ACTIONS.filter(x => b.includes(x) && !a.includes(x));
    if (added.length || removed.length) result.push({ module, added, removed });
  }
  return result;
}

export function samePermissions(a: ModulePermissions, b: ModulePermissions): boolean {
  return diffPermissions(a, b).length === 0;
}

export function roleSnapshot(
  role: Pick<CustomRole, 'name' | 'description' | 'permissions' | 'active'>
): RoleSnapshot {
  const permissions: ModulePermissions = {};
  for (const [m, actions] of Object.entries(role.permissions ?? {})) permissions[m] = [...actions];
  return { name: role.name, description: role.description, permissions, active: role.active };
}

/** authUid: uid de Firebase Auth; las reglas exigen que actor.uid sea request.auth.uid. */
export function buildRoleActor(
  user: { uid?: string; email?: string; name?: string; lastName?: string } | null | undefined,
  authUid?: string | null
): RoleActor {
  return {
    uid: authUid ?? user?.uid ?? '',
    email: user?.email ?? '',
    name: `${user?.name ?? ''} ${user?.lastName ?? ''}`.trim()
  };
}

export function normalizeRoleHistoryEntry(id: string, data: any): RoleHistoryEntry {
  return {
    id,
    type: data?.type,
    roleId: String(data?.roleId ?? ''),
    roleName: String(data?.roleName ?? ''),
    targetUser: data?.targetUser?.uid
      ? { uid: String(data.targetUser.uid), email: String(data.targetUser.email ?? '') }
      : null,
    before: data?.before ?? null,
    after: data?.after ?? null,
    actor: {
      uid: String(data?.actor?.uid ?? ''),
      email: String(data?.actor?.email ?? ''),
      name: String(data?.actor?.name ?? '')
    },
    at: toDate(data?.at)
  };
}
